import {
  Box,
  Button,
  Typography,
} from '@mui/material';
import { T, Icon, stitchCard } from './stitch';
import { formatDate } from './projectMeta';
import { buildGrippOfferUrl, buildGrippProjectUrl } from '../../../utils/grippDeepLink';


// "Open in Gripp": opdracht als die er is, anders de offerte (offertefase).
const grippUrlVoor = (link) => {
  if (!link?.gripp_id) return null;
  return link.type === 'project'
    ? buildGrippProjectUrl(link.gripp_id, link.company_id)
    : buildGrippOfferUrl(link.gripp_id, link.company_id);
};

const OpenInGripp = ({ link, sx }) => {
  const url = grippUrlVoor(link);
  if (!url) return null;
  return (
    <Button size="small" variant="outlined" href={url} target="_blank" rel="noreferrer"
      startIcon={<Icon name="open_in_new" size={16} />} sx={{ textTransform: 'none', ...sx }}>
      {link.type === 'project' ? 'Open opdracht in Gripp' : 'Open offerte in Gripp'}
    </Button>
  );
};


const euro = (n) =>
  typeof n === 'number' && isFinite(n)
    ? n.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : '';


/**
 * Offerteregels van het project — wat er verkocht is, per sectie.
 *
 * De regels komen uit de lokale spiegel van Airtable (die op zijn beurt uit Gripp
 * komt), niet live uit Gripp: dat scheelt een rondje langs twee externe API's bij
 * elke pageview. Daarom staat er onderaan wanneer de spiegel is bijgewerkt.
 */
function QuoteLinesPanel({ data, loading, error }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {[0, 1, 2, 3].map((i) => (
          <Box key={i} sx={{ height: 44, bgcolor: T.surfaceLow, borderRadius: '0.5rem' }} />
        ))}
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Icon name="error_outline" size={48} sx={{ color: T.outlineVariant, display: 'block', mx: 'auto', mb: 1 }} />
        <Typography sx={{ fontFamily: T.fontBody, color: T.outline }}>Offerteregels konden niet geladen worden</Typography>
      </Box>
    );
  }
  if (!data || !data.count) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Icon name="request_quote" size={48} sx={{ color: T.outlineVariant, display: 'block', mx: 'auto', mb: 1 }} />
        <Typography sx={{ fontFamily: T.fontBody, color: T.outline }}>Geen offerteregels gevonden voor dit project</Typography>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outlineVariant, mt: 0.5 }}>
          Regels verschijnen hier zodra de offerte in Gripp aan dit projectnummer hangt.
        </Typography>
        <OpenInGripp link={data?.gripp_link} sx={{ mt: 2 }} />
      </Box>
    );
  }

  const kolom = { fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const cel = { fontFamily: T.fontBody, fontSize: '0.82rem', color: T.text };
  // Op smal scherm vallen de tussenkolommen weg; omschrijving en totaal blijven.
  const grid = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr auto', md: 'minmax(0,1fr) 64px 80px 100px 72px 110px' },
    gap: 1,
    alignItems: 'baseline',
    px: 2,
    py: 1,
  };
  const alleenBreed = { display: { xs: 'none', md: 'block' } };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <OpenInGripp link={data.gripp_link} />
      </Box>
      <Box sx={{ ...stitchCard, overflow: 'hidden' }}>
        <Box sx={{ ...grid, borderBottom: `1px solid ${T.surfaceHigh}`, bgcolor: T.surfaceLow }}>
          <Typography sx={kolom}>Omschrijving</Typography>
          <Typography sx={{ ...kolom, ...alleenBreed, textAlign: 'right' }}>Aantal</Typography>
          <Typography sx={{ ...kolom, ...alleenBreed }}>Eenheid</Typography>
          <Typography sx={{ ...kolom, ...alleenBreed, textAlign: 'right' }}>Stukprijs</Typography>
          <Typography sx={{ ...kolom, ...alleenBreed, textAlign: 'right' }}>Uren</Typography>
          <Typography sx={{ ...kolom, textAlign: 'right' }}>Totaal</Typography>
        </Box>

        {data.groups.map((g) => (
          <Box key={g.group}>
            <Box sx={{ ...grid, bgcolor: T.surfaceLow, borderBottom: `1px solid ${T.surfaceHigh}` }}>
              <Typography sx={{ fontFamily: T.font, fontSize: '0.82rem', fontWeight: 700, color: T.text }}>{g.group}</Typography>
              <Box sx={alleenBreed} /><Box sx={alleenBreed} /><Box sx={alleenBreed} />
              <Typography sx={{ ...cel, ...alleenBreed, fontWeight: 600, textAlign: 'right' }}>
                {g.hours ? `${g.hours.toLocaleString('nl-NL')}u` : ''}
              </Typography>
              <Typography sx={{ ...cel, fontWeight: 700, textAlign: 'right' }}>{euro(g.total_price)}</Typography>
            </Box>
            {g.lines.map((l, li) => (
              <Box key={l.airtable_id || l.gripp_line_id || li} sx={{ ...grid, borderBottom: `1px solid ${T.surfaceLow}` }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ ...cel, overflowWrap: 'anywhere' }}>
                    {l.description_short || l.description || 'Regel'}
                  </Typography>
                  {l.description && l.description_short && l.description !== l.description_short && (
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.74rem', color: T.outline, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {l.description}
                    </Typography>
                  )}
                  {/* Op smal scherm staan aantal/prijs niet in eigen kolommen — hier alsnog. */}
                  <Typography sx={{ display: { xs: 'block', md: 'none' }, fontFamily: T.fontBody, fontSize: '0.74rem', color: T.outline }}>
                    {[l.quantity ? `${l.quantity.toLocaleString('nl-NL')} ${l.unit || ''}`.trim() : '',
                      l.price_per_unit ? `à ${euro(l.price_per_unit)}` : '',
                      l.hours ? `${l.hours.toLocaleString('nl-NL')}u` : ''].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                <Typography sx={{ ...cel, ...alleenBreed, textAlign: 'right' }}>
                  {l.quantity != null ? l.quantity.toLocaleString('nl-NL') : ''}
                </Typography>
                <Typography sx={{ ...cel, ...alleenBreed, color: T.outline }}>{l.unit || ''}</Typography>
                <Typography sx={{ ...cel, ...alleenBreed, textAlign: 'right' }}>{euro(l.price_per_unit)}</Typography>
                <Typography sx={{ ...cel, ...alleenBreed, textAlign: 'right', color: T.outline }}>
                  {l.hours ? l.hours.toLocaleString('nl-NL') : ''}
                </Typography>
                <Typography sx={{ ...cel, textAlign: 'right', fontWeight: 600 }}>{euro(l.total_price)}</Typography>
              </Box>
            ))}
          </Box>
        ))}

        <Box sx={{ ...grid, bgcolor: T.surfaceHigh }}>
          <Typography sx={{ fontFamily: T.font, fontSize: '0.85rem', fontWeight: 800, color: T.text }}>
            Totaal ({data.count} regels)
          </Typography>
          <Box sx={alleenBreed} /><Box sx={alleenBreed} /><Box sx={alleenBreed} />
          <Typography sx={{ ...cel, ...alleenBreed, fontWeight: 700, textAlign: 'right' }}>
            {data.total_hours ? `${data.total_hours.toLocaleString('nl-NL')}u` : ''}
          </Typography>
          <Typography sx={{ ...cel, fontWeight: 800, textAlign: 'right' }}>{euro(data.total_price)}</Typography>
        </Box>
      </Box>

      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.74rem', color: T.outlineVariant, mt: 1.5 }}>
        {data.source === 'gripp_offer'
          ? 'Bedragen excl. btw, van de Gripp-offerte (nog geen opdracht).'
          : 'Bedragen excl. btw, uit de offerte in Gripp.'}
        {data.synced_at ? ` Laatst gesynchroniseerd op ${formatDate(data.synced_at)}.` : ''}
      </Typography>
    </Box>
  );
}

export default QuoteLinesPanel;
